import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { RegexFormatter } from '@repo/toolkit/regex'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import { getEnrollmentEmails } from '../../../../api/enrollments/get-enrollment-emails'
import { SendEnrollmentQuotaInput } from '../../../../lib/types/models/enrollment/enrollment-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createStringArraySchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledMultiSelectInput from '../../../elements/inputs/controlled-multi-select-input'
import ValidationButtons from '../../../elements/validation-buttons'

type Props = BaseFormProps<SendEnrollmentQuotaInput> & {
  mode: 'quotation' | 'enrollment'
  enrollmentId: number
  className?: string
}

const formSchema: ZodType<SendEnrollmentQuotaInput> = z.object({
  emails: createStringArraySchema('emails')
})

export function SendEnrollmentProposalForm({
  mode,
  enrollmentId,
  defaultValues,
  className,
  ...validationButtonProps
}: Props) {
  const form = useForm<SendEnrollmentQuotaInput>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: FORM_MODE
  })

  const { data: emails } = useQuery({
    queryKey: ['enrollment-emails', enrollmentId],
    queryFn: () => getEnrollmentEmails(enrollmentId)
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn(className, 'flex flex-col gap-3')}>
          <Typography variant={'label-semibold'}>
            {mode === 'quotation'
              ? "Confirmer l'envoi du devis à l'email suivant :"
              : "Confirmer l'envoi de notre proposition aux emails suivants :"}
          </Typography>

          <FormField
            control={form.control}
            name={'emails'}
            render={({ field }) => (
              <FormItem className={'flex-1'}>
                <FormControl>
                  <ControlledMultiSelectInput
                    {...field}
                    searchable
                    allowNewOption
                    label={'Emails'}
                    variant={'warning'}
                    options={emails?.map((email) => ({ label: email, value: email }))}
                    selectedOptions={field.value?.map((email) => ({ label: email, value: email }))}
                    onChange={(options) => {
                      field.onChange(
                        options.filter(
                          (email) => email !== '' && RegexFormatter.isValidEmail(email)
                        ) || []
                      )
                    }}
                  />
                </FormControl>
              </FormItem>
            )}
          />
        </div>

        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}
